import posthog from "posthog-js";
import { POSTHOG_HOST, POSTHOG_KEY, isPostHogConfigured } from "./config";

let initialized = false;
let lastEvent: { name: string; at: number; props?: Record<string, unknown> } | null = null;

/**
 * Initialize PostHog (Product Analytics + Session Replay + Feature Flags +
 * Web Analytics autocapture). Idempotent and SSR-safe.
 *
 * Called from the startup orchestrator AFTER Sentry so PostHog inherits a
 * fully-booted error layer. If the key placeholder hasn't been replaced,
 * we no-op silently (still safe to import everywhere).
 */
export function initPostHog(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!isPostHogConfigured()) {
    // eslint-disable-next-line no-console
    console.info("[posthog] not configured — set POSTHOG_KEY in src/lib/posthog/config.ts");
    return;
  }
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask], [data-sensitive]",
    },
    loaded: () => {
      // Feature flags + web analytics initialize automatically; nothing to do here.
    },
  });
  initialized = true;
}

export function getPostHog(): typeof posthog | null {
  return initialized ? posthog : null;
}

export function isPostHogReady(): boolean {
  return initialized;
}

export function identifyUser(
  userId: string,
  traits: Record<string, unknown> = {},
): void {
  if (!initialized) return;
  try { posthog.identify(userId, traits); } catch { /* noop */ }
}

export function resetPostHog(): void {
  if (!initialized) return;
  try { posthog.reset(); } catch { /* noop */ }
}

export function capture(
  name: string,
  props: Record<string, unknown> = {},
): void {
  if (!initialized) return;
  try {
    posthog.capture(name, props);
    lastEvent = { name, at: Date.now(), props };
  } catch { /* noop */ }
}

export function getDistinctId(): string | null {
  if (!initialized) return null;
  try { return posthog.get_distinct_id(); } catch { return null; }
}

export function getActiveFeatureFlags(): string[] {
  if (!initialized) return [];
  try {
    const flags = (posthog.featureFlags as unknown as { getFlags?: () => string[] }).getFlags?.();
    return Array.isArray(flags) ? flags : [];
  } catch { return []; }
}

export function getLastEvent() {
  return lastEvent;
}

/** Safe wrappers for PostHog property / flag / session reads.
 *  These prevent internal `Object.entries(null)` crashes when PostHog
 *  is in a partially-initialised state. */
export function getFeatureFlags(): Record<string, boolean | string> {
  if (!initialized) return {};
  try {
    const flags = (posthog as unknown as { getFeatureFlags?: () => Record<string, boolean | string> | null }).getFeatureFlags?.();
    return flags ?? {};
  } catch { return {}; }
}

export function getProperty(key: string): unknown {
  if (!initialized) return undefined;
  try {
    return (posthog as unknown as { get_property?: (k: string) => unknown }).get_property?.(key);
  } catch { return undefined; }
}

export function getSessionId(): string | null {
  if (!initialized) return null;
  try {
    const id = (posthog as unknown as { get_session_id?: () => string | null }).get_session_id?.();
    return id ?? null;
  } catch { return null; }
}